import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: string | null;
}

/**
 * Campo de formulario con etiqueta, pista y error.
 *
 * La etiqueta se asocia por id generado, y el error se enlaza con
 * `aria-describedby` y se anuncia con `role="alert"`: sin eso, quien navega
 * con lector de pantalla no se entera de que el formulario ha fallado.
 */
export function Field({ label, hint, error, className, ...rest }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={['qr-field', className].filter(Boolean).join(' ')}>
      <label className="qr-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={['qr-field__input', error ? 'qr-field__input--error' : null]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint && !error ? (
        <p className="qr-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="qr-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
